import { cn } from '@/shared/cn';
// fork(oem v1.8): `ChatModelProvider` adds `'local'` — the ProjeX local engine.
import type { ChatModelProvider } from '@/versions/v1/shared/chat-types';

const NAME: Record<ChatModelProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  local: 'AiRis ProjeX',
};

type Props = {
  provider: ChatModelProvider;
  size?: number;
  className?: string;
};

function LetterFallback({
  letter,
  name,
  size,
  className,
}: {
  letter: string;
  name: string;
  size: number;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label={name}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md bg-zinc-100 font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.max(8, Math.round(size * 0.55)) }}
    >
      {letter}
    </span>
  );
}

function AnthropicLogo({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={NAME.anthropic}
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      className={cn('shrink-0 text-zinc-900 dark:text-zinc-100', className)}
    >
      <path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" />
    </svg>
  );
}

function OpenAILogo({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={NAME.openai}
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      className={cn('shrink-0 text-zinc-900 dark:text-zinc-100', className)}
    >
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  );
}

function GoogleLogo({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={NAME.google}
      xmlns="http://www.w3.org/2000/svg"
      fill="#4285F4"
      className={cn('shrink-0', className)}
    >
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
    </svg>
  );
}

/**
 * fork(oem v1.8): the ProjeX mark — the same accent ring and plus as
 * `BrandMark variant="mark"`, drawn at the logo slot's size.
 */
function LocalEngineLogo({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={NAME.local}
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', className)}
    >
      <circle cx="10" cy="13" r="6.5" fill="none" stroke="var(--accent)" strokeWidth="2.4" />
      <path
        d="M18 5v6M15 8h6"
        fill="none"
        stroke="var(--brand-secondary)"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ProviderLogo({ provider, size = 14, className }: Props) {
  switch (provider) {
    case 'local':
      return <LocalEngineLogo size={size} className={className} />;
    case 'anthropic':
      return <AnthropicLogo size={size} className={className} />;
    case 'openai':
      return <OpenAILogo size={size} className={className} />;
    case 'google':
      return <GoogleLogo size={size} className={className} />;
    default: {
      const name = (provider as string).charAt(0).toUpperCase();
      return <LetterFallback letter={name} name={provider} size={size} className={className} />;
    }
  }
}
